import { createElement, forwardRef, useId } from 'react';
import './Field.css';

const joinIds = (...ids) => ids.filter(Boolean).join(' ') || undefined;

export const Field = ({
  label,
  hint,
  error,
  required = false,
  children,
  id: providedId,
  className = '',
}) => {
  const generatedId = useId();
  const inputId = providedId || `rds-field-${generatedId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  if (!label) {
    throw new Error('Field requires a visible label.');
  }

  return (
    <div className={`rds-field ${className}`.trim()} data-invalid={Boolean(error) || undefined}>
      <label className="rds-field__label" htmlFor={inputId}>
        {label}
        {required && <span className="rds-field__required" aria-hidden="true"> *</span>}
      </label>
      {children({
        id: inputId,
        required,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': joinIds(hintId, errorId),
      })}
      {hint && <div className="rds-field__hint" id={hintId}>{hint}</div>}
      {error && <div className="rds-field__error" id={errorId} role="alert">{error}</div>}
    </div>
  );
};

export const Input = forwardRef(({ as = 'input', variant = 'default', className = '', ...props }, ref) => (
  createElement(as, {
    ...props,
    ref,
    className: `rds-input rds-focusable ${variant === 'quiet' ? 'rds-input--quiet' : ''} ${className}`.trim(),
  })
));

Input.displayName = 'Input';

export const Textarea = forwardRef(({ className = '', ...props }, ref) => (
  <textarea ref={ref} className={`rds-input rds-textarea rds-focusable ${className}`.trim()} {...props} />
));

Textarea.displayName = 'Textarea';

export default Field;
